import {
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useDispatch } from 'react-redux';
import { GiTakeMyMoney } from 'react-icons/gi';
import { MdOutlinePermContactCalendar, MdRemoveDone } from 'react-icons/md';
import { RiCalendarEventFill } from 'react-icons/ri';
import { VscCheckAll } from 'react-icons/vsc';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import aadharFrontDummy from './aadharFrontDummy.jpg';
import { fDate } from '../../utils/formatTime';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import { CommonApiforJourney } from '../services/RecommendationApi';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDisburseSelector from '../../store/Slice/Disburse/DisburseSelector';
import { getDecisionInfoData } from '../../store/Slice/Disburse/DisburseSlice';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';

const FieldBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 14px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  height: '100%'
}));

export default function ProcessDisburse() {
  const navigate = useNavigate();
  const [documentPreview, setDocumentPreview] = useState({
    popup: false,
    imgPreview: aadharFrontDummy
  });

  const [rejReasonState, setRejReasonState] = useState({
    popup: false,
    type: null,
    rejectionMsg: null,
    idd: null
  });

  const [openLoader, setOpenLoader] = useState(false);
  const [openApiError, setOpenApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [subPageStatusMap, setSubPageStatusMap] = useState({});

  const dispatch = useDispatch();
  const location = useLocation();
  const rowId = location.state || {};

  const { LoginData } = useLoginSelector();
  const { DisburseDecisionInfo } = useDisburseSelector();

  const getDisbirseViewAPi = useCallback(() => {
    const finalObject = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      applicationId: rowId?.applicationId
    };
    dispatch(getDecisionInfoData(finalObject));
  }, [LoginData, rowId, dispatch]);

  useEffect(() => {
    getDisbirseViewAPi();
  }, [getDisbirseViewAPi]);

  useEffect(() => {
    const storedData = sessionStorage.getItem('reportTypeId');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        const statusMap = {};
        parsedData[0]?.accessSubPagesMappings?.forEach((subPage) => {
          statusMap[subPage.subPageCode] = subPage.isEnable;
        });
        setSubPageStatusMap(statusMap);
      } catch (e) {
        console.error('Error reading subpages permissions', e);
      }
    }
  }, []);

  const isSubPageEnabled = (subPageCode) => subPageStatusMap[subPageCode] || false;

  const disburseApiFire = async (type, condition) => {
    const token = LoginData?.jwt;

    const basePayload = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };

    const payload = { ...basePayload };
    const methodName = 'POST';
    let endpoint = '';

    switch (type) {
      case 'saveDecisionInfo': {
        const prevData = rejReasonState;
        const decisionInfo = DisburseDecisionInfo?.disbursementDecisionInfoResponseModel || {};

        const isDisbursement = prevData?.type === 'Disbursement';
        const isRejected = condition === 'reject';

        if (isDisbursement) {
          Object.assign(payload, {
            disbursementAcceptance: !isRejected,
            disbursementId: prevData?.idd,
            disburseCell: rowId
          });
        } else {
          Object.assign(payload, {
            encashmentAcceptance: !isRejected,
            encashmentId: prevData?.idd,
            disbursedAmount: decisionInfo?.disbursedAmount ?? null,
            disbursedDate: decisionInfo?.disbursedDate ?? null,
            disburseCell: rowId
          });
        }

        endpoint = 'disbursed/saveDecisionInfo';
        break;
      }

      case 'getImageUrl': {
        try {
          const parsedData = JSON.parse(condition);
          Object.assign(payload, {
            applicationId: parsedData?.applicationId,
            fileName: parsedData?.fileName
          });
          endpoint = 'file/downloadFile';
        } catch (err) {
          console.error('Invalid condition JSON for getImageUrl', err);
          return;
        }
        break;
      }

      default:
        console.warn(`Unhandled API type: ${type}`);
        return;
    }

    disburseApiFireResponse(type, methodName, endpoint, JSON.stringify(payload), token, '');
  };

  const disburseApiFireResponse = async (
    type,
    method,
    endpoint,
    credentials,
    authToken,
    frmData
  ) => {
    setOpenLoader(true);
    const responseData = await CommonApiforJourney(
      method,
      endpoint,
      credentials,
      authToken,
      frmData
    );

    if (responseData.status === 'success' || responseData.action_status === 'success') {
      setOpenLoader(false);
      if (type === 'saveDecisionInfo') {
        if (rejReasonState.rejectionMsg === 'approve') {
          setOpenApiError(true);
          setErrorMessage(`${rejReasonState.type} Approved`);
          getDisbirseViewAPi();
        } else {
          navigate('/disburse');
        }
        setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null });
      }
    } else if (responseData.type === 'application/octet-stream') {
      setOpenLoader(false);
      const blobData = URL.createObjectURL(responseData);
      if (type === 'getImageUrl') {
        setDocumentPreview({ popup: true, imgPreview: blobData });
      }
    } else {
      setOpenLoader(false);
      setOpenApiError(true);
      setErrorMessage('Something went wrong !');
    }
  };

  const disbursementModel = DisburseDecisionInfo?.disbursementDecisionInfoResponseModel;
  const encashmentList = DisburseDecisionInfo?.encashmentDecisionInfoResponseModelList;

  return (
    <Page title="Partner Management System || Disburse">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <PageTitle info="Process Disbursement" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          {isValidValue(disbursementModel) && (
            <Card
              sx={{
                p: { xs: 2.5, md: 3 },
                mb: 3.5,
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                background: '#ffffff'
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  pb: 2,
                  mb: 2.5,
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      borderRadius: '4px',
                      backgroundColor: '#13519d'
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Disbursement Details
                  </Typography>
                </Stack>

                {isSubPageEnabled('DISBURSE_APPROVE') && (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Approve" placement="top" arrow>
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleOutlineIcon />}
                          disabled={Boolean(
                            isSubPageEnabled('DISBURSE_APPROVE') &&
                              disbursementModel?.acceptanceStatus
                          )}
                          onClick={() => {
                            const prevDatas = rejReasonState;
                            prevDatas.popup = false;
                            prevDatas.type = 'Disbursement';
                            prevDatas.rejectionMsg = 'approve';
                            prevDatas.idd = disbursementModel?.disbursementId;
                            setRejReasonState(prevDatas);
                            disburseApiFire('saveDecisionInfo', 'approve');
                          }}
                          sx={{
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { backgroundColor: '#059669' },
                            '&.Mui-disabled': {
                              backgroundColor: '#e2e8f0',
                              color: '#94a3b8'
                            }
                          }}
                        >
                          Approve
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Reject" placement="top" arrow>
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<HighlightOffIcon />}
                          disabled={Boolean(
                            isSubPageEnabled('DISBURSE_REJECT') &&
                              disbursementModel?.acceptanceStatus
                          )}
                          onClick={() => {
                            setRejReasonState({
                              popup: true,
                              type: 'Disbursement',
                              rejectionMsg: null,
                              idd: disbursementModel?.disbursementId
                            });
                          }}
                          sx={{
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { backgroundColor: '#dc2626' },
                            '&.Mui-disabled': {
                              backgroundColor: '#e2e8f0',
                              color: '#94a3b8'
                            }
                          }}
                        >
                          Reject
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldBox>
                    <GiTakeMyMoney size={26} color="#13519d" />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: '11px',
                          display: 'block'
                        }}
                      >
                        Disbursed Amount
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                      >
                        {disbursementModel.disbursedAmount
                          ? disbursementModel.disbursedAmount
                          : `${NOT_AVAILABLE}`}
                      </Typography>
                    </Box>
                  </FieldBox>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FieldBox>
                    <RiCalendarEventFill size={24} color="#13519d" />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: '11px',
                          display: 'block'
                        }}
                      >
                        Disbursed Date
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                      >
                        {disbursementModel.disbursedDate
                          ? fDate(disbursementModel.disbursedDate)
                          : `${NOT_AVAILABLE}`}
                      </Typography>
                    </Box>
                  </FieldBox>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FieldBox>
                    <MdOutlinePermContactCalendar size={24} color="#13519d" />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: '11px',
                          display: 'block'
                        }}
                      >
                        Submitted By
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                      >
                        {normalizeValue(disbursementModel.submittedBy, `${NOT_AVAILABLE}`)}
                      </Typography>
                    </Box>
                  </FieldBox>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FieldBox>
                    <RiCalendarEventFill size={24} color="#13519d" />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: '11px',
                          display: 'block'
                        }}
                      >
                        Submitted On
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                      >
                        {disbursementModel.submittedOn
                          ? fDate(disbursementModel.submittedOn)
                          : `${NOT_AVAILABLE}`}
                      </Typography>
                    </Box>
                  </FieldBox>
                </Grid>
              </Grid>

              {disbursementModel?.disbursementDisburseDocumentsDetailsModels?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      mb: 1.5
                    }}
                  >
                    Supporting Documents
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {disbursementModel.disbursementDisburseDocumentsDetailsModels.map(
                      (imgItem, index) => (
                        <Box
                          key={index}
                          onClick={() => {
                            const payloadObj = {
                              applicationId: DisburseDecisionInfo.applicationId,
                              fileName: imgItem.savedName
                            };
                            disburseApiFire('getImageUrl', JSON.stringify(payloadObj));
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '10px',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            color: '#166534',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: '#dcfce7',
                              borderColor: '#86efac',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <RemoveRedEyeIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {imgItem.fileName?.split('.')[0] || 'Document'}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Stack>
                </Box>
              )}
            </Card>
          )}

          {encashmentList &&
            encashmentList.map((itemL, idx) => (
              <Card
                key={idx}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  mb: 3.5,
                  borderRadius: '20px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                  background: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    pb: 2,
                    mb: 2.5,
                    borderBottom: '1px solid #f1f5f9'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        borderRadius: '4px',
                        backgroundColor: '#13519d'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Encashment {encashmentList.length > 1 ? `#${idx + 1}` : ''}
                    </Typography>
                  </Stack>

                  {isSubPageEnabled('DISBURSE_APPROVE') && (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Approve" placement="top" arrow>
                        <span>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={Boolean(itemL?.acceptanceStatus)}
                            onClick={() => {
                              const prevDatas = rejReasonState;
                              prevDatas.popup = false;
                              prevDatas.type = 'Encashment';
                              prevDatas.rejectionMsg = 'approve';
                              prevDatas.idd = itemL?.encashmentId;
                              setRejReasonState(prevDatas);
                              disburseApiFire('saveDecisionInfo', 'approve');
                            }}
                            sx={{
                              backgroundColor: '#10b981',
                              color: '#ffffff',
                              fontWeight: 600,
                              borderRadius: '8px',
                              textTransform: 'none',
                              px: 2,
                              '&:hover': { backgroundColor: '#059669' },
                              '&.Mui-disabled': {
                                backgroundColor: '#e2e8f0',
                                color: '#94a3b8'
                              }
                            }}
                          >
                            Approve
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject" placement="top" arrow>
                        <span>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<HighlightOffIcon />}
                            disabled={Boolean(itemL?.acceptanceStatus)}
                            onClick={() => {
                              setRejReasonState({
                                popup: true,
                                type: 'Encashment',
                                rejectionMsg: null,
                                idd: itemL?.encashmentId
                              });
                            }}
                            sx={{
                              backgroundColor: '#ef4444',
                              color: '#ffffff',
                              fontWeight: 600,
                              borderRadius: '8px',
                              textTransform: 'none',
                              px: 2,
                              '&:hover': { backgroundColor: '#dc2626' },
                              '&.Mui-disabled': {
                                backgroundColor: '#e2e8f0',
                                color: '#94a3b8'
                              }
                            }}
                          >
                            Reject
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>

                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FieldBox>
                      <GiTakeMyMoney size={26} color="#13519d" />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            display: 'block'
                          }}
                        >
                          Encashment Amount
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                        >
                          {itemL.encashmentAmount ? itemL.encashmentAmount : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </FieldBox>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FieldBox>
                      <RiCalendarEventFill size={24} color="#13519d" />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            display: 'block'
                          }}
                        >
                          Encashment Date
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                        >
                          {itemL.encashmentDate ? fDate(itemL.encashmentDate) : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </FieldBox>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FieldBox>
                      <MdOutlinePermContactCalendar size={24} color="#13519d" />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            display: 'block'
                          }}
                        >
                          Submitted By
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                        >
                          {itemL.submittedBy ? itemL.submittedBy : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </FieldBox>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FieldBox>
                      <RiCalendarEventFill size={24} color="#13519d" />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            display: 'block'
                          }}
                        >
                          Submitted On
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', mt: 0.25 }}
                        >
                          {itemL.submittedOn ? fDate(itemL.submittedOn) : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </FieldBox>
                  </Grid>
                </Grid>

                {itemL.encashmentDisburseDocumentsDetailsModels?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'block',
                        mb: 1.5
                      }}
                    >
                      Supporting Documents
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      {itemL.encashmentDisburseDocumentsDetailsModels.map((imgItem, index) => (
                        <Box
                          key={index}
                          onClick={() => {
                            const payloadObj = {
                              applicationId: DisburseDecisionInfo.applicationId,
                              fileName: imgItem.savedName
                            };
                            disburseApiFire('getImageUrl', JSON.stringify(payloadObj));
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '10px',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            color: '#166534',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: '#dcfce7',
                              borderColor: '#86efac',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <RemoveRedEyeIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {imgItem.fileName?.split('.')[0] || 'Document'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Card>
            ))}
        </Box>

        {/* Rejection Modal */}
        <Dialog
          open={rejReasonState.popup}
          onClose={() => {
            setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null });
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1.5,
              borderBottom: '1px solid #f1f5f9'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Rejection Reason
            </Typography>
            <IconButton
              onClick={() =>
                setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null })
              }
              size="small"
              sx={{ color: '#94a3b8' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <TextField
              fullWidth
              multiline
              value={rejReasonState.rejectionMsg || ''}
              onChange={(e) => {
                setRejReasonState((prev) => ({
                  ...prev,
                  rejectionMsg: e.target.value
                }));
              }}
              rows={4}
              placeholder="Please provide reason for rejection..."
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc'
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid #f1f5f9' }}>
            <Button
              onClick={() => {
                setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null });
              }}
              sx={{
                borderRadius: '8px',
                color: '#64748b',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => disburseApiFire('saveDecisionInfo', 'reject')}
              variant="contained"
              sx={{
                backgroundColor: '#ef4444',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: '#dc2626' }
              }}
            >
              Submit Rejection
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Preview Dialog */}
        <Dialog
          open={documentPreview.popup}
          onClose={() => setDocumentPreview({ popup: false, imgPreview: null })}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1.5,
              borderBottom: '1px solid #f1f5f9'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Document Preview
            </Typography>
            <IconButton
              onClick={() => setDocumentPreview({ popup: false, imgPreview: null })}
              size="small"
              sx={{ color: '#94a3b8' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              pt: 2.5,
              textAlign: 'center',
              backgroundColor: '#f8fafc',
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={documentPreview.imgPreview}
              alt="Document Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '65vh',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid #f1f5f9' }}>
            <Button
              onClick={() => setDocumentPreview({ popup: false, imgPreview: null })}
              variant="contained"
              sx={{
                backgroundColor: '#13519d',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: '#0d3b75' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <ApiErrorMessage
          openApiError={openApiError}
          setOpenApiError={setOpenApiError}
          errorMessage={errorMessage}
        />
        <Loader openLoader={openLoader} />
      </Container>
    </Page>
  );
}
